import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, LayoutDashboard, FileText, Settings, Database, Users, CreditCard, UserSearch, Phone, ChevronDown, ChevronRight, ClipboardCheck, ClipboardList, GitBranch, Menu, X, LogOut, Loader2, Terminal, Activity, Network, Blocks, TrendingUp, Wallet, Target, Award, ShieldCheck } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppAuth } from '../contexts/AuthContext';
import { getBaseOriginUrl } from '../utils/basePath';
import { clearSageSession } from '../utils/sageSession';
import SageChatPanel from './Sage/SageChatPanel';

// Icon map: DB stores icon name as string, we resolve to component here
const iconMap = {
  LayoutDashboard,
  Users,
  CreditCard,
  UserSearch,
  Phone,
  GitBranch,
  ClipboardCheck,
  ClipboardList,
  FileText,
  BarChart3,
  Database,
  Terminal,
  Settings,
  Activity,
  Network,
  Blocks,
  TrendingUp,
  Wallet,
  Target,
  Award,
  ShieldCheck,
};

const SECTION_LABELS = {
  overview: 'Overview',
  business: 'Business Intelligence',
  call_center: 'Call Center',
  issue_mgmt: 'Issue Management',
  engineering: 'Engineering',
  data: 'Data & Reporting',
  admin: 'Administration',
};

// Fallback navigation used while API data loads
const fallbackNavigation = [
  // --- Overview ---
  { name: 'Dashboard', icon: 'LayoutDashboard', href: '#dashboard', section: 'overview' },
  { name: 'APL Health', icon: 'ShieldCheck', href: '#apl-health', section: 'overview' },

  // --- Business Intelligence ---
  {
    name: 'Product Metrics', icon: 'TrendingUp', href: '#tenant-adoption', section: 'business',
    children: [
      { name: 'Tenant Adoption', href: '#tenant-adoption' },
      { name: 'Health Metrics', href: '#health-metrics' },
    ],
  },
  {
    name: 'Card Metrics', icon: 'CreditCard', href: '#card-metrics', section: 'business',
    children: [
      { name: 'Overview', href: '#card-metrics' },
      { name: 'Transaction Patterns', href: '#card-transaction-patterns' },
    ],
  },
  { name: 'Enrollment Checklist', icon: 'ClipboardCheck', href: '#enrollment-checklist', section: 'business' },
  {
    name: 'Program Design', icon: 'Blocks', href: '#master-action-catalog', section: 'business',
    children: [
      { name: 'Master Action Catalog', href: '#master-action-catalog' },
    ],
  },
  {
    name: 'Purse Design', icon: 'Wallet', href: '#acc-learn', section: 'business',
    children: [
      { name: 'Learn: How It Works', href: '#acc-learn' },
      { name: 'Setup Wizard', href: '#acc-wizard' },
      { name: 'Config Builder', href: '#acc-config' },
      { name: 'Guided Builder', href: '#acc-guided' },
    ],
  },

  // --- Call Center ---
  {
    name: 'Call Center', icon: 'Phone', href: '#call-center-dashboard', section: 'call_center',
    children: [
      { name: 'Dashboard', href: '#call-center-dashboard' },
      { name: 'Daily Health Summary', href: '#call-center-daily-health' },
      { name: 'Agent Productivity', href: '#call-center-agent-productivity' },
      { name: 'Call Intent Patterns', href: '#call-center-intent-patterns' },
      { name: 'Live Calls', href: '#live-calls' },
      { name: 'QA Evaluation', href: '#qa-dashboard' },
    ],
  },

  // --- Issue Management ---
  {
    name: 'Issue Management', icon: 'UserSearch', href: '#consumer-summary', section: 'issue_mgmt',
    children: [
      { name: 'Consumer Search', href: '#consumer-summary' },
      { name: 'Card Status History', href: '#card-status-history' },
      { name: 'Financial Transactions', href: '#consumer-financial-transactions' },
      { name: 'Eligibility Data', href: '#agent-ops-eligibility-data' },
      { name: 'Case Pattern Analysis', href: '#call-center-case-patterns' },
      { name: 'APL Lookup', href: '#fis-apl-lookup' },
      { name: 'Merchant Network', href: '#fis-merchant-network' },
      // ADM-1009 prototype — remove on cleanup / merge decline
      { name: '[PROTOTYPE] Card Ordering Audit', href: '#card-ordering-audit' },
    ],
  },

  // --- Print Production ---
  {
    name: 'Print Production', icon: 'CreditCard', href: '#print-ops-health', section: 'issue_mgmt',
    children: [
      { name: 'Ops Health', href: '#print-ops-health' },
      { name: 'File Tracker', href: '#print-ops-file-tracker' },
    ],
  },

  // --- Engineering ---
  {
    name: 'Technology', icon: 'GitBranch', href: '#technology', section: 'engineering',
    children: [
      { name: 'Executive Dashboard', href: '#technology' },
      { name: 'Sprint Goals', href: '#sprint-goals' },
      { name: 'Platform', href: '#technology-sprint-platform' },
      { name: 'Client Portal', href: '#technology-sprint-clientPortal' },
      { name: 'Analytics', href: '#technology-sprint-analytics' },
      { name: 'Service Cloud', href: '#technology-sprint-serviceCloud' },
      { name: 'Maintenance & Support', href: '#technology-kanban-maintenanceSupport' },
      { name: 'DevOps', href: '#technology-kanban-devops' },
      { name: 'Marketing Cloud', href: '#technology-kanban-marketingCloud' },
      { name: 'Release Readiness', href: '#technology-release-readiness' },
    ],
  },
  {
    name: 'Service Registry', icon: 'Network', href: '#registry', section: 'engineering',
    children: [
      { name: 'Service Catalog', href: '#registry' },
      { name: 'Dependency Map', href: '#registry-graph' },
      { name: 'Gap Dashboard', href: '#registry-gaps' },
    ],
  },
  {
    name: 'Tech Support', icon: 'Terminal', href: '#query', section: 'engineering',
    children: [
      { name: 'Custom Query', href: '#query' },
      { name: 'Log Search', href: '#tech-support' },
      { name: 'Log Patterns', href: '#log-patterns' },
      { name: 'Reconciliation Report', href: '#reconciliation-report' },
      { name: 'Reward Integrity Checks', href: '#reward-integrity-checks' },
    ],
  },

  // --- Data & Reporting ---
  {
    name: 'DataOps', icon: 'Database', href: '#eligibility-check', section: 'data',
    children: [
      { name: 'Eligibility Check', href: '#eligibility-check' },
      { name: 'File Integrity', href: '#file-integrity' },
      { name: 'Prefunding Report', href: '#prefunding-report' },
      { name: 'Generate Card 30', href: '#fis-card30' },
      { name: 'Generate Card 35', href: '#fis-card35' },
    ],
  },
  { name: 'Reports', icon: 'FileText', href: '#reports', section: 'data' },
  { name: 'Analytics', icon: 'BarChart3', href: '#analytics', section: 'data' },
  { name: 'Sweepstakes Drawing', icon: 'Award', href: '#sweepstakes-drawing', section: 'data' },

  // --- Administration ---
  {
    name: 'Administration', icon: 'Settings', href: '#admin-users', section: 'admin',
    children: [
      { name: 'User Management', href: '#admin-users' },
      { name: 'Roles & Permissions', href: '#admin-roles' },
    ],
  },
];

function Sidebar({ isOpen, onClose }) {
  const [expandedItem, setExpandedItem] = useState(null);
  const { navigation: apiNav, loading } = useAppAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Use API navigation if loaded, otherwise fallback
  const navigation = apiNav && apiNav.length > 0 ? apiNav : fallbackNavigation;

  // Derive active section from the current URL. Root maps to "dashboard"; for
  // every other path, strip the leading slash so existing href tokens like
  // "#registry-graph" continue to match. The /v2 prefix is handled by the
  // BrowserRouter basename, so location.pathname is already prefix-free.
  const activeSection = location.pathname === '/' ? 'dashboard' : location.pathname.slice(1);

  // Accordion: expanding a group collapses any other open group.
  const toggleExpand = (itemName) => {
    setExpandedItem((prev) => (prev === itemName ? null : itemName));
  };

  const isItemActive = (item) => {
    if (item.children) {
      return item.children.some((child) => activeSection === child.href.replace('#', ''))
        || (item.href === '#registry' && activeSection.startsWith('registry'));
    }
    return activeSection === item.href.replace('#', '');
  };

  const handleNavigation = (section) => {
    const resolved = section === 'qa-call-evaluation' ? 'qa-dashboard' : section;
    navigate(`/${resolved}`);
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
      <div className="flex h-16 items-center justify-between px-5 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <img src="/favicon.svg" alt="Sunny Benefits" className="w-9 h-9" />
          <span className="font-bold text-lg text-gray-900">LENS</span>
        </div>
        <button
          onClick={onClose}
          className="md:hidden p-1 rounded-lg hover:bg-gray-100"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <nav className="mt-2 px-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 128px)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : (
        <ul className="space-y-0.5">
          {navigation.map((item, index) => {
            const IconComponent = iconMap[item.icon] || LayoutDashboard;
            const active = item.children ? isItemActive(item) : activeSection === item.href.replace('#', '');
            const prevSection = index > 0 ? navigation[index - 1].section : null;
            const showSectionHeader = item.section && item.section !== prevSection;

            return (
              <li key={item.name}>
                {showSectionHeader && (
                  <div className={`px-3 pt-4 ${index > 0 ? 'mt-2 border-t border-gray-200' : ''}`}>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      {SECTION_LABELS[item.section] || item.section}
                    </span>
                  </div>
                )}
                {item.children ? (
                  <div>
                    <button
                      onClick={() => {
                        toggleExpand(item.name);
                        handleNavigation(item.href.replace('#', ''));
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? 'bg-amber-400 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent className={`w-5 h-5 ${active ? 'text-gray-900' : 'text-gray-500'}`} />
                        {item.name}
                      </div>
                      {expandedItem === item.name ? (
                        <ChevronDown className={`w-4 h-4 ${active ? 'text-gray-700' : 'text-gray-400'}`} />
                      ) : (
                        <ChevronRight className={`w-4 h-4 ${active ? 'text-gray-700' : 'text-gray-400'}`} />
                      )}
                    </button>
                    {expandedItem === item.name && (
                      <ul className="mt-0.5 ml-5 space-y-0.5 border-l border-gray-200">
                        {item.children.map((child) => {
                          const childActive = activeSection === child.href.replace('#', '');
                          return (
                            <li key={child.name}>
                              <button
                                onClick={() => handleNavigation(child.href.replace('#', ''))}
                                className={`w-full flex items-center gap-2.5 pl-4 pr-3 py-1.5 text-sm transition-colors ${
                                  childActive
                                    ? 'text-amber-700 font-semibold'
                                    : 'text-gray-500 hover:text-gray-800'
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  childActive ? 'bg-amber-500' : 'bg-gray-300'
                                }`} />
                                {child.name}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavigation(item.href.replace('#', ''))}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'bg-amber-400 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <IconComponent className={`w-5 h-5 ${active ? 'text-gray-900' : 'text-gray-500'}`} />
                    {item.name}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        )}
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
        <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors">
          <Settings className="w-5 h-5" />
          Settings
        </button>
      </div>
    </aside>
    </>
  );
}

function useAuth0Safe() {
  try {
    return useAuth0();
  } catch {
    return { user: { name: 'Local Dev', email: 'dev@local' }, logout: () => {} };
  }
}

const SageStarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="none">
    <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41Z"/>
  </svg>
);

function Header({ onMenuClick, sageOpen, onSageToggle }) {
  const { user, logout } = useAuth0Safe();
  const { profile, hasPermission } = useAppAuth();

  const initials = user
    ? (user.given_name?.[0] || user.name?.[0] || user.email?.[0] || 'U').toUpperCase() +
      (user.family_name?.[0] || '').toUpperCase()
    : 'SB';

  const roleBadge = profile?.roleNames?.[0];

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-8">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2">
          <img src="/favicon.svg" alt="Sunny Benefits" className="w-6 h-6 md:hidden" />
          <span className="md:hidden">LENS</span>
          <span className="hidden md:inline">LENS – Leveraging Engineering Numbers & Statistics</span>
        </h1>
      </div>
      <div className="flex items-center gap-2 md:gap-4">
        <span className="hidden sm:block text-sm text-gray-400">
          Last updated: {new Date().toLocaleTimeString()}
        </span>
        {hasPermission('sage:access') && (
          <button
            onClick={onSageToggle}
            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium
                       rounded-full px-4 py-1.5 flex items-center gap-2 transition-colors"
          >
            {sageOpen ? (
              <>
                <X className="w-3.5 h-3.5" />
                Close SAGE
              </>
            ) : (
              <>
                <SageStarIcon />
                Ask SAGE
              </>
            )}
          </button>
        )}
        <div className="flex items-center gap-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || 'User'}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center font-semibold text-xs">
              {initials}
            </div>
          )}
          <div className="hidden md:flex flex-col">
            <span className="text-sm text-gray-700 font-medium max-w-[120px] truncate leading-tight">
              {user?.name || user?.email}
            </span>
            {roleBadge && (
              <span className="text-[10px] text-amber-600 font-medium leading-tight">{roleBadge}</span>
            )}
          </div>
          <button
            onClick={() => {
              clearSageSession();
              logout({ logoutParams: { returnTo: getBaseOriginUrl() } });
            }}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

// Build version - update this when deploying
const BUILD_VERSION = '2026.03.09.1';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sageOpen, setSageOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="md:pl-64 flex-1 flex flex-col">
        <Header onMenuClick={() => setSidebarOpen(true)} sageOpen={sageOpen} onSageToggle={() => setSageOpen(!sageOpen)} />
        <main className="p-4 md:p-8 flex-1">{children}</main>
        <footer className="px-4 py-2 text-center text-xs text-gray-400 border-t border-gray-200 bg-white">
          LENS Analytics v{BUILD_VERSION}
        </footer>
      </div>

      {/* Sunny SAGE AI Chat */}
      <SageChatPanel isOpen={sageOpen} onClose={() => setSageOpen(false)} />
    </div>
  );
}
